import { IconButton } from '@/components/basic/icon-button';
import TrashIcon from '@/assets/icons/trash.svg';
import { gql } from '@/__generated__';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { Button } from '@/components/shadcn/button';
import { Spinner } from '@/components/basic/spinner';
import { Cross1Icon } from '@radix-ui/react-icons';
import { OrganizationGeoLocationV2 } from '@/__generated__/graphql';
import { useToast } from '@/components/shadcn/use-toast';
import { logError } from '@/lib/rum';

type ServedLocationItemProps = {
  location: Pick<OrganizationGeoLocationV2, 'id' | 'locationName'>;
};

const RemoveOrganizationGeoLocationMutation =
  gql(`mutation RemoveOrganizationGeoLocationMutation($input: RemoveOrganizationGeoLocationInput!) {
    grantSettings {
      removeOrganizationGeoLocation(input: $input) {
        success
      }
    }
  }`);

export default function ServedLocationItem({
  location,
}: ServedLocationItemProps) {
  const { toast } = useToast();
  const [isDelete, setIsDelete] = useState<boolean>(false);

  const [removeOrGeoLocation, { loading }] = useMutation(
    RemoveOrganizationGeoLocationMutation,
    {
      refetchQueries: ['FetchGrantSettings'],
      onCompleted: () => {
        toast({ duration: 3000, description: 'Removed location successfully' });
      },
      onError(error) {
        logError('Failed to remove organization geo-location', error);
        toast({
          description: 'Failed to remove location',
          duration: 3000,
          variant: 'destructive',
        });
      },
    }
  );

  return (
    <div className='mb-2 flex items-center gap-1'>
      {isDelete ? (
        <>
          <div className='col-span-18 flex flex-grow items-center justify-between rounded-md bg-destructive py-1 pl-3 pr-1 text-destructive-foreground'>
            <p className='truncate'>
              Are you sure you want to delete {location.locationName}?
            </p>
            <Button
              className='disabled:opacity-1 h-fit bg-destructive-foreground px-2 py-0.5 text-destructive hover:bg-destructive-foreground/90 disabled:bg-destructive-foreground/80'
              disabled={loading}
              onClick={async (e) => {
                e.stopPropagation();

                removeOrGeoLocation({
                  variables: { input: { id: location.id } },
                });
              }}
            >
              {loading ? <Spinner className='h-6 w-6' /> : 'Yes, Delete'}
            </Button>
          </div>
          <Button
            className='h-full max-h-10 w-full max-w-10 border-none shadow-none hover:bg-transparent'
            onClick={(e) => {
              e.stopPropagation();
              setIsDelete(false);
            }}
          >
            <Cross1Icon className='text-[#707070]' />
          </Button>
        </>
      ) : (
        <>
          <div className='flex-grow rounded-sm bg-gray-settings-button/80 px-3 py-1'>
            {location.locationName}
          </div>
          <IconButton
            className='h-full max-h-10 w-full max-w-10 border-none shadow-none hover:bg-transparent'
            src={TrashIcon}
            onClick={() => setIsDelete(true)}
          />
        </>
      )}
    </div>
  );
}
